import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { FamilyRestroom } from '@mui/icons-material';

import { ToneChip } from '../../common/StatusChip';
import type { ApplicationSiblings } from '../../../services/peopleService';
import { familyName, matchedByPhrase, siblingDetails, siblingSummary } from '../../families/familyFormat';

interface Props {
  data: ApplicationSiblings;
  /** Opens the Family tab, where the siblings are listed. */
  onClick: () => void;
}

/** "Sibling at school" beside the application's status, so it shows on every tab. */
const SiblingsChip: React.FC<Props> = ({ data, onClick }) => {
  const summary = siblingSummary(data.siblings);
  if (!summary) return null;

  const tooltip = (
    <Box sx={{ py: 0.25 }}>
      {data.siblings.map(s => (
        <Typography key={s._id} variant="caption" component="div">
          <Box component="span" fontWeight={600}>{s.name}</Box>
          {siblingDetails(s).map(d => `  ·  ${d}`).join('')}
        </Typography>
      ))}
      {data.basis === 'guardian_cnic' && data.family && (
        <Typography variant="caption" component="div" sx={{ mt: 0.75, opacity: 0.8 }}>
          Found by the {matchedByPhrase(data.matchedGuardians)} CNIC, which belongs to the {familyName(data.family)} family.
          Confirmed once the family is linked.
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow slotProps={{ tooltip: { sx: { maxWidth: 440 } } }}>
      <span>
        <ToneChip
          label={summary.label}
          tone={summary.tone}
          icon={FamilyRestroom}
          onClick={onClick}
          aria-label={`${summary.label}. Show family`}
          sx={{ cursor: 'pointer' }}
        />
      </span>
    </Tooltip>
  );
};

export default React.memo(SiblingsChip);
